import { PortfolioDto } from "../../../screens/portfolio-info/types/PortfolioDto";
import { PortfolioProfile } from "../../types/PortfolioProfile";
import { useAppContext } from "../../contexts/app-context/components/AppContext";
import { LocalStorageConstants } from "../../constants/LocalStorageConstants";
import { useEffect, useMemo, useState } from "react";
import { PortfolioModel } from "../../../screens/portfolio-info/types/PortfolioModel";
import { portfolioMappers } from "../../mappers/PortfolioMappers";
import { UnderlyingAssetDto } from "../../../screens/portfolio-info/types/UnderlyingAssetDto";
import { useAuthContext } from "../../../screens/authentication/hooks/use-auth/AuthContext";
import { useNavigate } from "react-router-dom";
import { PortfolioDates } from "./PortfolioDates";
import { AllPortfoliosConstants } from "../../../screens/portfolio-info/constants/AllPortfoliosConstants";
import { allPortfoliosBuilder } from "../../../screens/portfolio-info/utils/AllPortfoliosBuilder";
import { PortfolioGroupDto } from "../../../screens/portfolio-info/types/PortfolioGroupDto";
import { PortfolioGroupCreateDto } from "../../../screens/portfolio-info/types/PortfolioGroupCreateDto";
import { PortfolioGroupModel } from "../../../screens/portfolio-info/types/PortfolioGroupModel";
import { PortfolioGroupConstants } from "../../../screens/portfolio-info/constants/PortfolioGroupConstants";
import { PortfolioGroupAddOrUpdateModel } from "../../../screens/portfolio-info/types/PortfolioGroupAddOrUpdateModel";
import { createAuthenticatedApiClient } from "../../services/api/AuthenticatedApiClient";
import { createUnderlyingAssetApi } from "../../services/api/UnderlyingAssetApi";
import { createPortfolioGroupApi } from "../../services/api/PortfolioGroupApi";
import { createUnderlyingAssetPortfolioApi } from "../../services/api/UnderlyingAssetPortfolioApi";
import { createPortfolioApi } from "../../services/api/PortfolioApi";
import { portfolioProfileService } from "../../services/portfolio-profile-service/PortfolioProfileService";

export function usePortfolioCommonService() {
    const [underlyingAssetDto, setUnderlyingAssetDto] = useState<UnderlyingAssetDto | null>();
    const [portfolioGroups, setPortfolioGroups] = useState<PortfolioGroupModel[]>([]);
    const [selectedPortfolioGroup, setSelectedPortfolioGroup] = useState<PortfolioGroupModel | null>(null);
    const [portfolios, setPortfolios] = useState<PortfolioModel[]>([]);
    const [underlyingAssetPortfolios, setUnderlyingAssetPortfolios] = useState<PortfolioModel[]>([]);
    const [selectedPortfolio, setSelectedPortfolio] = useState<PortfolioModel | null>(null);
    const [isAllPortfoliosSelected, setIsAllPortfoliosSelected] = useState<boolean>(false);
    const [selectedPortfolioDates, setSelectedPortfolioDates] = useState<PortfolioDates | null>(null);
    const [selectedComparedPortfolio, setSelectedComparedPortfolio] = useState<PortfolioModel | null>(null);

    // todo: PortfolioProfile and PortfolioSettings
    const [portfolioProfile, setPortfolioProfile] = useState<PortfolioProfile>({
        data: []
    });
    
    // todo: PortfolioProfile and PortfolioSettings
    const [comparedPortfolioProfile, setComparedPortfolioProfile] = useState<PortfolioProfile>({
        data: []
    });
    
    const { marketDataDto } = useAppContext();
    const { userAuthData, isUserAuthenticated } = useAuthContext();
    const navigate = useNavigate();
    const apiClient = useMemo(
        () => createAuthenticatedApiClient({ userAuthData, isUserAuthenticated, navigate }),
        [userAuthData, isUserAuthenticated, navigate],
    );
    const underlyingAssetApi = useMemo(() => createUnderlyingAssetApi(apiClient), [apiClient]);
    const portfolioGroupApi = useMemo(() => createPortfolioGroupApi(apiClient), [apiClient]);
    const underlyingAssetPortfolioApi = useMemo(() => createUnderlyingAssetPortfolioApi(apiClient), [apiClient]);
    const portfolioApi = useMemo(() => createPortfolioApi(apiClient), [apiClient]);
    
    useEffect(() => {
        fetchPortfolios();
    }, [marketDataDto]);

    async function fetchPortfolios(): Promise<void> {
        if (!isUserAuthenticated()) {
            navigate('/login');
            return;
        }
        
        if (!marketDataDto) {
            return;
        }
        
        const currentUnderlyingAssetName = marketDataDto.BaseAsset.BaseAsset;
        let underlyingAssetDto = await underlyingAssetApi.getByName(currentUnderlyingAssetName);

        if (underlyingAssetDto == null) {
            underlyingAssetDto = await underlyingAssetApi.create(currentUnderlyingAssetName);
        }

        if (underlyingAssetDto == null) {
            return;
        }
        
        setUnderlyingAssetDto(underlyingAssetDto);

        const selectedPortfolioGroup = await fetchPortfolioGroupsByUnderlyingAsset(underlyingAssetDto);

        if (selectedPortfolioGroup === null) {
            return;
        }

        await fetchPortfoliosByUnderlyingAsset(underlyingAssetDto);
        await fetchPortfoliosByGroup(underlyingAssetDto, selectedPortfolioGroup);
    }

    async function fetchPortfolioGroupsByUnderlyingAsset(
        underlyingAssetDto: UnderlyingAssetDto,
    ): Promise<PortfolioGroupModel | null> {
        const portfolioGroupDtos = await portfolioGroupApi.getByUnderlyingAsset(underlyingAssetDto.Id);

        if (portfolioGroupDtos === null) {
            return null;
        }

        const portfolioGroupModels = portfolioGroupDtos.map(x => portfolioMappers.mapPortfolioGroup(x));
        setPortfolioGroups(portfolioGroupModels);

        const storedPortfolioGroupName = localStorage.getItem(LocalStorageConstants.SelectedPortfolioGroupName);
        const selectedGroup =
            portfolioGroupModels.find(x => x.name === storedPortfolioGroupName) ??
            portfolioGroupModels.find(x => x.name === PortfolioGroupConstants.DefaultName) ??
            portfolioGroupModels[0] ??
            null;

        if (selectedGroup === null) {
            setSelectedPortfolioGroup(null);
            return null;
        }

        localStorage.setItem(LocalStorageConstants.SelectedPortfolioGroupName, selectedGroup.name);
        setSelectedPortfolioGroup(selectedGroup);

        return selectedGroup;
    }

    async function fetchPortfoliosByGroup(
        underlyingAssetDto: UnderlyingAssetDto,
        portfolioGroup: PortfolioGroupModel,
    ): Promise<void> {
        if (!marketDataDto) {
            return;
        }

        const portfolioDtos = await underlyingAssetPortfolioApi.getByUnderlyingAsset(
            underlyingAssetDto.Id,
            portfolioGroup.id,
        );

        if (portfolioDtos == null) {
            return;
        }

        const portfolioModels = portfolioDtos.map(x =>
            portfolioMappers.map(x, marketDataDto.OptionInfos, marketDataDto.UnderlyingPrice));

        setPortfolios(portfolioModels);
        await trySetSelectedPortfolio(portfolioModels, portfolioGroup, underlyingAssetDto);
    }

    async function fetchPortfoliosByUnderlyingAsset(underlyingAssetDto: UnderlyingAssetDto): Promise<void> {
        if (!marketDataDto) {
            return;
        }

        const portfolioDtos = await underlyingAssetPortfolioApi.getByUnderlyingAsset(underlyingAssetDto.Id);

        if (portfolioDtos == null) {
            return;
        }

        setUnderlyingAssetPortfolios(portfolioDtos.map(x =>
            portfolioMappers.map(x, marketDataDto.OptionInfos, marketDataDto.UnderlyingPrice)));
    }

    async function trySetSelectedPortfolio(
        portfolioModels: PortfolioModel[],
        portfolioGroup: PortfolioGroupModel | null = selectedPortfolioGroup,
        currentUnderlyingAssetDto: UnderlyingAssetDto | null = underlyingAssetDto ?? null,
    ): Promise<void> {
        if (portfolioModels === null || portfolioModels.length === 0) {
            setSelectedPortfolio(null);
            setIsAllPortfoliosSelected(false);
            return;
        }
        
        let selectedPortfolioName = localStorage.getItem(LocalStorageConstants.SelectedPortfolioName);
        if (selectedPortfolioName === AllPortfoliosConstants.StorageValue) {
            await setAllPortfoliosByUnderlyingAsset(portfolioGroup, currentUnderlyingAssetDto);
            return;
        }

        let portfolioModel = portfolioModels.find(x => x.name === selectedPortfolioName);
        
        if (portfolioModel === undefined || portfolioModel === null) {
            portfolioModel = portfolioModels[0];
        }

        await setSelectedPortfolioByName(portfolioModel);
    }

    async function setSelectedPortfolioByName(portfolioModel: PortfolioModel): Promise<void> {
        setIsAllPortfoliosSelected(false);

        if (portfolioModel.modifiedAt === null && portfolioModel.id !== 0) {
            const portfolioDto = await getPortfolioById(portfolioModel.id);

            if (portfolioDto == null || !marketDataDto) {
                return;
            }

            portfolioModel.portfolioBalanceHistory = portfolioDto.PortfolioBalanceHistory.map(portfolioMappers.mapPortfolioBalanceHistoryEntry);
            portfolioModel.positions = portfolioDto.Positions.map(x =>
                portfolioMappers.mapPosition(x, marketDataDto.OptionInfos, marketDataDto.UnderlyingPrice));
            portfolioModel.orders = portfolioDto.Orders.map(x =>
                portfolioMappers.mapOrder(x, marketDataDto.OptionInfos, marketDataDto.UnderlyingPrice));
            portfolioModel.portfolioSettings = portfolioMappers.mapSettings(portfolioDto.PortfolioSettings);
        }
        
        localStorage.setItem(LocalStorageConstants.SelectedPortfolioName, portfolioModel.name);
        
        setSelectedPortfolio(portfolioModel);
        refreshPortfolioProfile(portfolioModel);
        
        // todo: set compared portfolio
        const settings = portfolioModel?.portfolioSettings;
        
        if (!settings) {
            return;
        }
        
        if (settings.useComparedPortfolio && settings.comparedPortfolioId !== null && marketDataDto) {
            const comparedPortfolioDto = await getPortfolioById(settings.comparedPortfolioId);
            
            if (comparedPortfolioDto === null) {
                setComparedPortfolioProfile({...selectedComparedPortfolio, data: []});
                return;
            }
            
            if (!settings.useComparedPortfolio) {
                setComparedPortfolioProfile({...selectedComparedPortfolio, data: []});
            }
            
            const comparedPortfolioModel = portfolioMappers.map(comparedPortfolioDto, marketDataDto.OptionInfos, marketDataDto.UnderlyingPrice);
            setSelectedComparedPortfolio(comparedPortfolioModel);
            
            if (settings.useComparedPortfolio) {
                refreshComparedPortfolioProfile(portfolioModel, comparedPortfolioModel);
            }
        }
    }

    async function setAllPortfoliosByUnderlyingAsset(
        portfolioGroup: PortfolioGroupModel | null = selectedPortfolioGroup,
        currentUnderlyingAssetDto: UnderlyingAssetDto | null = underlyingAssetDto ?? null,
    ): Promise<void> {
        if (!isUserAuthenticated()) {
            navigate('/login');
            return;
        }

        if (!currentUnderlyingAssetDto || !marketDataDto || !portfolioGroup) {
            return;
        }

        const fullPortfolioDtos = await underlyingAssetPortfolioApi.getByUnderlyingAsset(
            currentUnderlyingAssetDto.Id,
            portfolioGroup.id,
            true,
        );

        if (fullPortfolioDtos === null) {
            return;
        }

        const fullPortfolioModels = fullPortfolioDtos.map(x =>
            portfolioMappers.map(x, marketDataDto.OptionInfos, marketDataDto.UnderlyingPrice));
        const aggregatePortfolio = allPortfoliosBuilder.build(
            fullPortfolioModels,
            portfolioMappers.mapUnderlyingAsset(currentUnderlyingAssetDto),
            portfolioGroup);

        localStorage.setItem(LocalStorageConstants.SelectedPortfolioName, AllPortfoliosConstants.StorageValue);
        setIsAllPortfoliosSelected(true);
        setSelectedComparedPortfolio(null);
        setComparedPortfolioProfile({...comparedPortfolioProfile, data: []});
        setSelectedPortfolio(aggregatePortfolio);
        refreshPortfolioProfile(aggregatePortfolio);

        const settings = aggregatePortfolio.portfolioSettings;

        if (settings?.useComparedPortfolio && settings.comparedPortfolioId !== null) {
            const comparedPortfolioDto = await getPortfolioById(settings.comparedPortfolioId);

            if (comparedPortfolioDto !== null) {
                const comparedPortfolioModel = portfolioMappers.map(
                    comparedPortfolioDto,
                    marketDataDto.OptionInfos,
                    marketDataDto.UnderlyingPrice);

                setSelectedComparedPortfolio(comparedPortfolioModel);
                refreshComparedPortfolioProfile(aggregatePortfolio, comparedPortfolioModel);
            }
        }
    }

    async function setSelectedPortfolioGroupByName(portfolioGroup: PortfolioGroupModel): Promise<void> {
        if (!underlyingAssetDto) {
            return;
        }

        localStorage.setItem(LocalStorageConstants.SelectedPortfolioGroupName, portfolioGroup.name);
        setSelectedPortfolioGroup(portfolioGroup);
        setSelectedComparedPortfolio(null);
        setComparedPortfolioProfile({...comparedPortfolioProfile, data: []});

        await fetchPortfoliosByUnderlyingAsset(underlyingAssetDto);
        await fetchPortfoliosByGroup(underlyingAssetDto, portfolioGroup);
    }

    async function createPortfolioGroup(portfolioGroupModel: PortfolioGroupAddOrUpdateModel): Promise<void> {
        if (!isUserAuthenticated()) {
            navigate('/login');
            return;
        }

        if (!underlyingAssetDto) {
            return;
        }

        const portfolioGroupDto: PortfolioGroupCreateDto = {
            Id: 0,
            Name: portfolioGroupModel.name,
            UnderlyingAsset: underlyingAssetDto,
            PortfolioSettings: {
                UsePopupForStepChangeSelector: portfolioGroupModel.usePopupForStepChangeSelector,
            },
        };

        const createdPortfolioGroupId = await portfolioGroupApi.create(underlyingAssetDto.Id, portfolioGroupDto);

        if (createdPortfolioGroupId === null) {
            return;
        }

        localStorage.setItem(LocalStorageConstants.SelectedPortfolioGroupName, portfolioGroupModel.name);
        const createdPortfolioGroup = await fetchPortfolioGroupsByUnderlyingAsset(underlyingAssetDto);

        if (createdPortfolioGroup !== null) {
            await fetchPortfoliosByGroup(underlyingAssetDto, createdPortfolioGroup);
        }
    }

    async function editSelectedPortfolioGroup(portfolioGroupModel: PortfolioGroupAddOrUpdateModel): Promise<void> {
        if (!isUserAuthenticated()) {
            navigate('/login');
            return;
        }

        if (!underlyingAssetDto || !selectedPortfolioGroup) {
            return;
        }

        const portfolioSettings = portfolioMappers.mapSettingsModelToDto(selectedPortfolioGroup.portfolioSettings);

        if (portfolioSettings !== null) {
            portfolioSettings.UsePopupForStepChangeSelector = portfolioGroupModel.usePopupForStepChangeSelector;
        }

        const portfolioGroupDto: PortfolioGroupDto = {
            Id: selectedPortfolioGroup.id,
            Name: portfolioGroupModel.name,
            UnderlyingAsset: underlyingAssetDto,
            PortfolioSettings: portfolioSettings,
        };

        const updatedPortfolioGroupDto = await portfolioGroupApi.update(portfolioGroupDto);
        const updatedPortfolioGroup = updatedPortfolioGroupDto !== null
            ? portfolioMappers.mapPortfolioGroup(updatedPortfolioGroupDto)
            : null;

        if (updatedPortfolioGroup === null) {
            return;
        }

        setPortfolioGroups(portfolioGroups.map(portfolioGroup =>
            portfolioGroup.id === updatedPortfolioGroup!.id
                ? updatedPortfolioGroup!
                : portfolioGroup));

        await setSelectedPortfolioGroupByName(updatedPortfolioGroup);
    }

    async function deleteSelectedPortfolioGroup(): Promise<void> {
        if (!isUserAuthenticated()) {
            navigate('/login');
            return;
        }

        if (!underlyingAssetDto || !selectedPortfolioGroup) {
            return;
        }

        const isDeleted = await portfolioGroupApi.deleteById(selectedPortfolioGroup.id);

        if (!isDeleted) {
            return;
        }

        const nextSelectedGroup = await fetchPortfolioGroupsByUnderlyingAsset(underlyingAssetDto);

        if (nextSelectedGroup !== null) {
            await fetchPortfoliosByGroup(underlyingAssetDto, nextSelectedGroup);
            return;
        }

        setPortfolioGroups([]);
        setSelectedPortfolioGroup(null);
        setPortfolios([]);
        setUnderlyingAssetPortfolios([]);
        setSelectedPortfolio(null);
    }

    // todo: refactor. Similar with setSelectedPortfolioByName
    async function setSelectedComparedPortfolioByName(portfolioModel: PortfolioModel): Promise<void> {
        if (portfolioModel.modifiedAt === null && portfolioModel.id !== 0) {
            const portfolioDto = await getPortfolioById(portfolioModel.id);

            if (portfolioDto == null || !marketDataDto) {
                return;
            }

            portfolioModel.orders = portfolioDto.Orders.map(
                x => portfolioMappers.mapOrder(x, marketDataDto.OptionInfos, marketDataDto.UnderlyingPrice));
            portfolioModel.portfolioSettings = portfolioMappers.mapSettings(portfolioDto.PortfolioSettings);
        }

        setSelectedComparedPortfolio(portfolioModel);
        refreshComparedPortfolioProfile(selectedPortfolio!, portfolioModel);
    }
    
    async function getPortfolioById(id: number): Promise<PortfolioDto | null> {
        if (!isUserAuthenticated()) {
            navigate('/login');
            return null;
        }

        return await portfolioApi.getById(id);
    }
    
    function refreshPortfolioProfile(portfolio: PortfolioModel | null): void {
        const result = portfolioProfileService.buildProfile(portfolio);

        setPortfolioProfile(result.profile);
        setSelectedPortfolioDates(result.portfolioDates);
    }
    
    function refreshComparedPortfolioProfile(selectedPortfolio: PortfolioModel, comparedPortfolio: PortfolioModel): void {
        setComparedPortfolioProfile(portfolioProfileService.buildComparedProfile(selectedPortfolio, comparedPortfolio));
    }
    
    return {
        underlyingAssetDto,
        marketDataDto,
        portfolioGroups,
        setPortfolioGroups,
        selectedPortfolioGroup,
        setSelectedPortfolioGroup,
        setSelectedPortfolioGroupByName,
        createPortfolioGroup,
        editSelectedPortfolioGroup,
        deleteSelectedPortfolioGroup,
        portfolios,
        underlyingAssetPortfolios,
        setUnderlyingAssetPortfolios,
        setPortfolios,
        fetchSlimPortfolios: fetchPortfolios,
        selectedPortfolio,
        setSelectedPortfolio,
        isAllPortfoliosSelected,
        setIsAllPortfoliosSelected,
        setSelectedPortfolioByName,
        setAllPortfoliosByUnderlyingAsset,
        selectedComparedPortfolio,
        setSelectedComparedPortfolio,
        setSelectedComparedPortfolioByName,
        refreshPortfolioProfile,
        portfolioProfile,
        comparedPortfolioProfile,
        refreshComparedPortfolioProfile,
        setComparedPortfolioProfile,
        selectedPortfolioDates,
    };
}
